import { Box } from '@mui/material';
import {
  calculateContractFinancials,
  calculateProjectCategoryFinancials,
  ownHours,
  targetIsOperative,
} from '@timelog/pm-shared';
import type { ContractInput } from '@timelog/pm-shared';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFinancials } from './editor';
import {
  factorTrend,
  formatFactorAs,
  formatMetricValue,
  metricFormula,
  metricLabel,
} from './metricReading';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { ContractInsightHeading } from '@/components/planning/ContractInsightHeading';
import { contractOwnFactor } from '@/components/planning/contractStaffing';
import {
  FigureTooltip,
  MICRO_LABEL_TRACKING,
  SectionHeading,
  SidePanelHeading,
} from '@/components/ui';
import { formatAmount, formatAmountAbbrev, formatHours } from '@/lib';

/**
 * The Financials panel's project overview, behind "Show project summary" on
 * the collapse row (3 Nov, Mette) — the whole project through the budget's
 * lens, as Planning's overview is through the plan's: the same title, the
 * same way in, tab-specific sections. Finances: the CALC-16 totals across every
 * contract (forecast mirrors budget in v1), with "View by Category" folding out
 * the same rollup per finance category. Contracts: one row per contract — its
 * heading as Planning's Contract insights show it, its revenue, cost and hours, and
 * its budget factor (or margin) against its target — the comparison someone
 * budgeting reads across contracts. The open contract's row is marked, as
 * the contracts list marks it; a row opens that contract's view.
 */

const LineItem = ({
  label,
  value,
  strong = false,
  automationId,
}: {
  label: string;
  value: string;
  strong?: boolean;
  automationId?: string;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      py: 1,
      borderBottom: strong ? 'none' : 'var(--border-width-thin) solid var(--border-divider)',
    }}
  >
    <Box
      component="span"
      sx={{
        fontSize: 11,
        color: strong ? 'var(--fg-1)' : 'var(--fg-2)',
        fontWeight: strong ? 500 : 400,
      }}
    >
      {label}
    </Box>
    <Box
      component="span"
      data-automation-id={automationId}
      sx={{
        fontFamily: 'var(--font-family-mono)',
        fontSize: strong ? 16 : 14,
        fontWeight: strong ? 500 : 400,
        color: 'var(--fg-1)',
      }}
    >
      {value}
    </Box>
  </Box>
);

export const ProjectOverviewAside = () => {
  const {
    state,
    projectFinancials,
    budgetModel,
    calcOptions,
    effectiveContracts,
    blendedCostRate,
    actions,
  } = useFinancials();
  const { t } = useTranslation(['pmFinancials', 'pmCommon']);
  const metric = budgetModel.config.metric;
  const [byCategoryOpen, setByCategoryOpen] = useState(false);
  const forecastedLabel =
    metric === 'factor' ? t('pmCommon:term.ForecastedFactor') : t('pmCommon:term.ForecastedMargin');
  const showTarget = targetIsOperative(budgetModel.config);
  const inputs = useMemo<ContractInput[]>(
    () => effectiveContracts.map(({ id, name, type, document }) => ({ id, name, type, document })),
    [effectiveContracts],
  );
  // CALC-16 by category, over the same contracts the headline sums.
  const byCategory = useMemo(
    () => calculateProjectCategoryFinancials(inputs, blendedCostRate, calcOptions),
    [inputs, blendedCostRate, calcOptions],
  );
  // CALC-9..14 per contract, for the Contracts rows.
  const perContract = useMemo(
    () =>
      inputs.map((contract) => ({
        contract,
        financials: calculateContractFinancials(contract.document, blendedCostRate, calcOptions),
      })),
    [inputs, blendedCostRate, calcOptions],
  );

  return (
    <Box data-automation-id="budget-aside" data-view="project">
      <Box sx={{ pt: 2, mb: 4 }} data-automation-id="project-overview-heading">
        <SidePanelHeading name={t('pmCommon:term.ProjectSummary')} />
      </Box>

      {/* Finances: the project totals (CALC-16). */}
      <Box
        data-automation-id="aside-project-section"
        sx={{ pb: 6, borderBottom: 'var(--border-width-thin) solid var(--border-divider)' }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <SectionHeading icon="summarize">{t('pmCommon:term.Financials')}</SectionHeading>
          <Box component="span" sx={{ fontSize: 11, color: 'var(--fg-3)' }}>
            {t('contracts.Count', { count: projectFinancials.contractCount })}
          </Box>
        </Box>

        <LineItem
          label={t('aside.HoursWork')}
          value={t('units.Hours', { value: formatHours(projectFinancials.ownHours) })}
          automationId="aside-hours"
        />
        <LineItem
          label={t('pmCommon:term.Cost')}
          value={`${formatAmountAbbrev(projectFinancials.cost)} DKK`}
          automationId="aside-cost"
        />
        <LineItem
          label={t('pmCommon:term.Revenue')}
          value={`${formatAmountAbbrev(projectFinancials.revenue)} DKK`}
          strong
          automationId="aside-revenue"
        />

        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <FigureTooltip
            title={
              metric === 'factor'
                ? t('help.ProjectBudgetedFactor')
                : t('help.ProjectBudgetedMargin')
            }
          >
            <Box
              tabIndex={0}
              data-automation-id="aside-factor-box"
              sx={{
                flex: 1,
                bgcolor: 'var(--bg-sunken)',
                borderRadius: 'var(--border-radius-medium)',
                px: 1.5,
                py: 1,
              }}
            >
              <Box
                sx={{
                  fontSize: 10,
                  color: 'var(--fg-3)',
                  textTransform: 'uppercase',
                  letterSpacing: MICRO_LABEL_TRACKING,
                  mb: 0.5,
                }}
              >
                {metric === 'factor'
                  ? t('pmCommon:term.BudgetedFactor')
                  : t('pmCommon:term.BudgetedMargin')}
              </Box>
              <Box
                data-automation-id="aside-factor"
                data-metric={metric}
                sx={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 20,
                  fontWeight: 500,
                  color: 'var(--fg-1)',
                  lineHeight: 1,
                }}
              >
                {formatMetricValue(metric, projectFinancials)}
              </Box>
              <Box
                sx={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 10,
                  color: 'var(--fg-3)',
                  mt: 0.5,
                }}
              >
                {projectFinancials.avgRate !== null
                  ? t('units.AmountPerHour', {
                      amount: formatAmount(Math.round(projectFinancials.avgRate)),
                    })
                  : '—'}
              </Box>
            </Box>
          </FigureTooltip>
          <FigureTooltip title={t('help.ProjectForecast')}>
            <Box
              tabIndex={0}
              data-automation-id="aside-forecast-box"
              sx={{
                flex: 1,
                border: 'var(--border-width-thin) dashed var(--border-divider)',
                borderRadius: 'var(--border-radius-medium)',
                px: 1.5,
                py: 1,
              }}
            >
              <Box
                sx={{
                  fontSize: 10,
                  color: 'var(--fg-3)',
                  textTransform: 'uppercase',
                  letterSpacing: MICRO_LABEL_TRACKING,
                  mb: 0.5,
                }}
              >
                {forecastedLabel}
              </Box>
              <Box
                data-automation-id="aside-forecast"
                sx={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 20,
                  fontWeight: 500,
                  color: 'var(--fg-3)',
                  lineHeight: 1,
                }}
              >
                {formatFactorAs(metric, projectFinancials.forecastedFactor)}
              </Box>
            </Box>
          </FigureTooltip>
        </Box>
        <Box
          data-automation-id="aside-metric-formula"
          sx={{ fontSize: 10, color: 'var(--fg-3)', mt: 1, lineHeight: 1.45 }}
        >
          {metricFormula(metric, t)} {t('aside.ForecastMirrors')}
        </Box>

        {/*
          View by Category (CALC-16 by category): the headline split per
          finance category in use across the contracts — revenue, cost and the
          budgeted factor (or margin) each earns; Own Work also its hours and
          average rate. Folded by default so the totals stay a glance.
        */}
        <Box
          component="button"
          type="button"
          data-automation-id="aside-by-category-toggle"
          aria-expanded={byCategoryOpen}
          onClick={() => setByCategoryOpen((open) => !open)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 1.5,
            px: 1,
            py: 0.5,
            border: 'var(--border-width-thin) solid var(--border-divider)',
            borderRadius: 'var(--border-radius-pill)',
            bgcolor: byCategoryOpen ? 'var(--bg-sunken)' : 'transparent',
            fontSize: 11,
            fontFamily: 'var(--font-family)',
            color: byCategoryOpen ? 'var(--fg-1)' : 'var(--fg-3)',
            cursor: 'pointer',
            '&:hover': { borderColor: 'var(--border-subtle)' },
          }}
        >
          <MaterialSymbol name={byCategoryOpen ? 'expand_less' : 'expand_more'} size={14} />
          {t('aside.ViewByCategory')}
        </Box>
        {byCategoryOpen ? (
          <Box data-automation-id="aside-by-category" sx={{ mt: 1 }}>
            {byCategory.length === 0 ? (
              <Box sx={{ fontSize: 11, color: 'var(--fg-3)' }}>{t('aside.NoCategory')}</Box>
            ) : (
              byCategory.map((row) => (
                <Box
                  key={row.id}
                  data-automation-id="aside-category-row"
                  data-category={row.id}
                  sx={{
                    py: 1,
                    borderTop: 'var(--border-width-thin) solid var(--border-divider)',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ fontSize: 11, fontWeight: 500, color: 'var(--fg-1)' }}
                    >
                      {t(`pmCommon:category.${row.id}`)}
                    </Box>
                    <Box
                      component="span"
                      data-automation-id="aside-category-factor"
                      title={t('aside.CategoryMetricTitle', {
                        metric: metricLabel(metric, t),
                        category: t(`pmCommon:category.${row.id}`),
                        count: row.contractCount,
                      })}
                      sx={{
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 14,
                        fontWeight: 500,
                        color: 'var(--fg-1)',
                      }}
                    >
                      {formatMetricValue(metric, row)}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 1,
                      fontFamily: 'var(--font-family-mono)',
                      fontSize: 10,
                      color: 'var(--fg-3)',
                      mt: 0.5,
                    }}
                  >
                    <Box component="span">
                      {t('aside.CategoryRevenueCost', {
                        revenue: formatAmountAbbrev(row.revenue),
                        cost: formatAmountAbbrev(row.cost),
                      })}
                    </Box>
                    {row.hours !== undefined ? (
                      <Box component="span" data-automation-id="aside-category-hours">
                        {t('aside.CategoryHours', {
                          hours: formatHours(row.hours),
                          rate:
                            row.avgRate === null || row.avgRate === undefined
                              ? '—'
                              : t('units.AmountPerHour', {
                                  amount: formatAmount(Math.round(row.avgRate)),
                                }),
                        })}
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              ))
            )}
          </Box>
        ) : null}
      </Box>

      {/* Contracts: one row per contract, in the list's order; a row opens it. */}
      <Box data-automation-id="aside-contracts-section" sx={{ pt: 6, pb: 2 }}>
        <Box sx={{ mb: 2 }}>
          <SectionHeading icon="contract">{t('pmCommon:term.Contracts')}</SectionHeading>
        </Box>
        {perContract.map(({ contract, financials }) => {
          const target = showTarget ? contract.document.targetFactor : null;
          const trend = factorTrend(financials.budgetedFactor, target);
          return (
            <Box
              key={contract.id}
              component="button"
              type="button"
              data-automation-id="aside-contract-row"
              data-contract-id={contract.id}
              data-active={contract.id === state.contractId ? 'true' : 'false'}
              aria-current={contract.id === state.contractId ? 'true' : undefined}
              onClick={() => void actions.selectContract(contract.id)}
              sx={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                font: 'inherit',
                // The open contract, as the contracts list marks it (3 Nov, Mette).
                bgcolor: contract.id === state.contractId ? 'var(--color-fog-10)' : 'transparent',
                border: 'none',
                borderTop: 'var(--border-width-thin) solid var(--border-divider)',
                px: 1,
                py: 1.5,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'var(--bg-hover)' },
                '&:focus-visible': { outline: 'var(--focus-ring)', outlineOffset: '-2px' },
              }}
            >
              <ContractInsightHeading
                contract={contract}
                metric={metric}
                ownFactor={contractOwnFactor(contract, budgetModel.config)}
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  mt: 0.5,
                }}
              >
                <Box
                  component="span"
                  data-automation-id="aside-contract-revenue-cost"
                  sx={{ fontFamily: 'var(--font-family-mono)', fontSize: 10, color: 'var(--fg-3)' }}
                >
                  {/* Hours as the contracts list shows them (3 Nov, Mette). */}
                  {t('aside.ContractRevenueCostHours', {
                    revenue: formatAmountAbbrev(financials.revenue),
                    cost: formatAmountAbbrev(financials.cost),
                    hours:
                      contract.document.revenue > 0
                        ? t('units.Hours', {
                            value: formatHours(ownHours(contract.document, blendedCostRate)),
                          })
                        : '—',
                  })}
                </Box>
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    component="span"
                    data-automation-id="aside-contract-factor"
                    sx={{
                      fontFamily: 'var(--font-family-mono)',
                      fontSize: 14,
                      fontWeight: 500,
                      color: 'var(--fg-1)',
                    }}
                  >
                    {formatMetricValue(metric, financials)}
                  </Box>
                  <MaterialSymbol name={trend.icon} size={16} color={trend.color} />
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
